import { useEffect } from 'react';
import { Actions } from '../components';
import CardLocation from '../components/CardLocation/CardLocation';
import ContactsList from '../components/ContactsList/ContactsList';
import NavBar from '../components/main/NavBar';
import UserProfileImage from '../components/UserProfilePicture/UserProfileImage';
import { ICardLocation, IEmployee } from '../interfaces';
import { loadNunitoSans } from '../utils/loadNunitoSans';
import type { ContextLocation, TemplateProps, VcardContextData } from './contract';

// Sicherer Platzhalter für eine fehlende Standort-Adresse (kein Standort ODER
// Standort ohne address) — verhindert, dass Bestandskomponenten (Actions,
// CardLocation) auf undefined-Feldern crashen. Adresse ist am Standort im
// Schreibpfad Pflicht (siehe specs/2026-09-11-standorte-als-liste-design.md
// §3); der Platzhalter bleibt trotzdem, weil der Kontrakt `address` locker
// typisiert (Record<string, unknown>) und damit read-seitig nicht garantiert.
const SAFE_ADDRESS_PLACEHOLDER = { team: '', city: '', street: '', houseNumber: '', zip: '', latitude: '0', longtitude: '0' };

// Adapter Kontrakt-Kontext → bisheriges IEmployee-Format der Bestandskomponenten.
// IEmployee.teams bleibt der HAUPTSTANDORT (locations[0]) — Actions/.vcf-Export
// kennen nur einen Ort (vgl. specs/2026-09-11-standorte-als-liste-design.md §3).
// Fuer die vollstaendige Liste siehe toCardLocations() weiter unten.
function toEmployee(data: VcardContextData): IEmployee {
  const { vcard, brand } = data;
  const hauptstandort = vcard.locations?.[0];
  return {
    externalId: '',
    urlKey: '',
    // Bewusst OHNE toRawBase64: Das Profilbild kommt als URL.
    base64ProfileImage: vcard.profileImage || '',
    role: vcard.role || '',
    email: vcard.email || '',
    firstName: vcard.firstName || '',
    lastName: vcard.lastName || '',
    phone: vcard.phone || '',
    mobile: vcard.mobile || '',
    whatsapp: vcard.whatsapp || '',
    facebook: vcard.facebook || '',
    website: vcard.website || '',
    linkedin: vcard.linkedin || '',
    instagram: vcard.instagram || '',
    provenExpert: vcard.provenExpert || '',
    companyName: vcard.companyName || '',
    brandKey: {
      brandName: brand.name || '',
      // Logo als VOLLE data:-URI durchreichen (korrekte MIME, inkl. SVG) — NavBar
      // rendert `src` direkt. Nur das Profilbild bleibt rohes base64 (vcf-Export).
      base64Logo: brand.logo || '',
      primaryColor: brand.primaryColor || '',
    },
    // IEmployee.teams ist nicht optional (Bestandskomponente `Actions` liest ungeguarded
    // teams.address.*) — ohne Standort (oder mit Standort, aber ohne address) einen
    // leeren, sicheren Platzhalter liefern statt undefined, damit Actions nicht crasht.
    // address ist im Kontrakt bewusst locker typisiert (Record<string, unknown>),
    // IAddress verlangt konkrete Felder — Cast über `unknown` nötig (tsc), Shape ist
    // strukturell identisch.
    teams: hauptstandort
      ? ({
          name: hauptstandort.name || '',
          // Standort-Logo als volle data:-URI (korrekte MIME, inkl. SVG) → CardLocation `src` direkt.
          base64TeamLogo: hauptstandort.logo || '',
          address: hauptstandort.address ?? SAFE_ADDRESS_PLACEHOLDER,
          openingHours: hauptstandort.openingHours || [],
        } as unknown as IEmployee['teams'])
      : ({
          name: '',
          base64TeamLogo: '',
          address: SAFE_ADDRESS_PLACEHOLDER,
          openingHours: [],
        } as IEmployee['teams']),
  } as IEmployee;
}

// Alle Standorte für die Kartenliste — anders als toEmployee().teams (nur der
// Hauptstandort) liefert diese Funktion JEDEN Eintrag, damit ClassicTemplate
// je Standort eine eigene CardLocation rendern kann.
function toCardLocations(locations: ContextLocation[] | undefined): ICardLocation[] {
  if (!locations) return [];
  return locations.map(
    (ort) =>
      ({
        name: ort.name || '',
        base64TeamLogo: ort.logo || '',
        address: ort.address ?? SAFE_ADDRESS_PLACEHOLDER,
        openingHours: ort.openingHours || [],
        byAppointment: ort.byAppointment,
      }) as unknown as ICardLocation,
  );
}

export function ClassicTemplate({ data, onEvent }: TemplateProps) {
  // Nunito Sans wird erst hier angefordert (T-04, Spec §2.4) — sonst würde sie
  // auf JEDER Karte geladen, auch dort, wo ein Marken-Template rendert und
  // kein einziges Element sie zeigt. Synchron im Render (nicht in useEffect),
  // damit die Anfrage so früh wie möglich startet, bevor überhaupt gemalt wird.
  loadNunitoSans();
  const employee = toEmployee(data);
  const primaryColor = data.brand.primaryColor || '#000000';
  const base64Logo = data.brand.logo || '';
  const locations = toCardLocations(data.vcard.locations);

  // Bestands-Konvention: Map & Buttons lesen die Brand-Farbe aus sessionStorage.
  sessionStorage.setItem('primaryColor', primaryColor);
  useEffect(() => {
    sessionStorage.setItem('primaryColor', primaryColor);
  }, [primaryColor]);

  return (
    <div style={{ backgroundColor: '#f5f5f5', height: '100%' }}>
        <NavBar logo={base64Logo} />

        <UserProfileImage
          profileImage={employee.base64ProfileImage}
          name={`${employee.firstName} ${employee.lastName}`}
          role={employee.role}
          company={employee.companyName}
        />

        <Actions employee={employee} />

        <ContactsList
          contacts={{
            email: employee.email,
            phone: employee.phone,
            mobile: employee.mobile,
            whatsapp: employee.whatsapp,
            facebook: employee.facebook,
            linkedin: employee.linkedin,
            website: employee.website,
            // Sichtbarer Text vs. tel:-Link (Block-B-Review Punkt A): der
            // Kontrakt liefert die national formatierte Anzeige separat vom
            // E.164-Rohwert (employee.phone/.mobile) — direkt aus data.vcard,
            // weil IEmployee (Bestandstyp) diese Felder nicht kennt.
            phoneDisplay: data.vcard.phoneDisplay,
            mobileDisplay: data.vcard.mobileDisplay,
          }}
          onEvent={onEvent}
        />

        {/* Je Standort eine eigene Karte, untereinander — bei genau einem
            Standort ist das optisch identisch zur bisherigen Einzelkarte.
            `byAppointment` steuert je Karte den Hinweis, unabhaengig davon,
            ob die Karte Oeffnungszeiten zeigt (siehe CardLocation). */}
        {locations.map((standort, i) => (
          <CardLocation key={`${standort.name}-${i}`} {...standort} />
        ))}

        <div
          style={{
            textAlign: 'center',
            fontSize: '10px',
            color: '#999',
            padding: '8px 0 16px 0',
            marginTop: '8px',
          }}
        >
          Map data © CARTO © OpenStreetMap
        </div>
      </div>
  );
}
